import { useState } from 'react';
import { Alert, Image, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import AuthInput from '../components/AuthInput';
import GradientBackground from '../components/GradientBackground';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { createProfile, saveSelectedProfile } from '../api/profiles';
import { avatarOptions } from '../utils/avatars';

type Props = NativeStackScreenProps<RootStackParamList, 'CreateChildProfile'>;

const ages = Array.from({ length: 12 }, (_, index) => index + 1);

export default function CreateChildProfileScreen({ navigation }: Props) {
  const [nickname, setNickname] = useState('');
  const [age, setAge] = useState(5);
  const [avatar, setAvatar] = useState(avatarOptions[0].id);
  const [nameError, setNameError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!nickname.trim()) {
      setNameError('Vui lòng nhập tên ở nhà của bé.');
      return;
    }

    setNameError('');
    setLoading(true);
    try {
      const profile = await createProfile({
        nickname: nickname.trim(),
        age,
        avatar_url: avatar,
      });
      await saveSelectedProfile(profile);
      navigation.reset({ index: 0, routes: [{ name: 'ProfileSelect' }] });
    } catch (error) {
      Alert.alert(
        'Chưa tạo được hồ sơ',
        error instanceof Error ? error.message : 'Vui lòng kiểm tra thông tin và thử lại.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.screen}>
      <GradientBackground fullHeight />
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <Text style={styles.title}>Tạo hồ sơ cho bé</Text>
        <Text style={styles.subtitle}>Chọn tên, tuổi và avatar có sẵn trong ứng dụng.</Text>

        <View style={styles.panel}>
          <AuthInput
            label="Tên ở nhà của bé"
            placeholder="Ví dụ: Bông, Sóc, Bin"
            value={nickname}
            onChangeText={(value) => {
              setNickname(value);
              if (nameError) setNameError('');
            }}
          />
          {nameError ? <Text style={styles.errorText}>{nameError}</Text> : null}

          <Text style={styles.label}>Tuổi của bé</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.ageList}>
            {ages.map((item) => (
              <Pressable
                key={item}
                style={[styles.ageButton, age === item && styles.ageButtonActive]}
                onPress={() => setAge(item)}
              >
                <Text style={[styles.ageText, age === item && styles.ageTextActive]}>{item}</Text>
              </Pressable>
            ))}
          </ScrollView>

          <Text style={styles.label}>Chọn avatar</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.avatarList}>
            {avatarOptions.map((item) => (
              <Pressable
                key={item.id}
                style={[styles.avatarButton, { backgroundColor: item.color }, avatar === item.id && styles.avatarActive]}
                onPress={() => setAvatar(item.id)}
              >
                <Image source={item.source} style={styles.avatarImage} resizeMode="contain" />
              </Pressable>
            ))}
          </ScrollView>

          <Pressable style={styles.mainButton} onPress={handleSubmit} disabled={loading}>
            <Text style={styles.mainButtonText}>{loading ? 'Đang lưu...' : 'Hoàn tất'}</Text>
          </Pressable>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'rgb(242, 249, 253)',
  },
  container: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 76,
    paddingBottom: 36,
  },
  title: {
    color: colors.buttonText,
    fontSize: 28,
    fontWeight: '900',
    marginBottom: 8,
  },
  subtitle: {
    color: colors.buttonText,
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
    marginBottom: 24,
  },
  panel: {
    backgroundColor: 'rgba(255,255,255,0.94)',
    borderRadius: 26,
    padding: 18,
  },
  label: {
    color: colors.textSecondary,
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 10,
  },
  errorText: {
    color: colors.danger,
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 18,
    marginTop: -10,
    marginBottom: 14,
  },
  ageList: {
    gap: 10,
    paddingBottom: 18,
  },
  ageButton: {
    width: 44,
    height: 44,
    borderRadius: 14,
    backgroundColor: colors.background,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border,
  },
  ageButtonActive: {
    backgroundColor: colors.button,
    borderColor: colors.button,
  },
  ageText: {
    color: colors.textPrimary,
    fontSize: 16,
    fontWeight: '900',
  },
  ageTextActive: {
    color: colors.buttonText,
  },
  avatarList: {
    gap: 14,
    paddingBottom: 22,
  },
  avatarButton: {
    width: 88,
    height: 88,
    borderRadius: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 4,
    borderColor: '#FFFFFF',
  },
  avatarActive: {
    borderColor: colors.blue,
  },
  avatarImage: {
    width: 72,
    height: 72,
  },
  mainButton: {
    height: 50,
    borderRadius: 18,
    backgroundColor: colors.button,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mainButtonText: {
    color: colors.buttonText,
    fontSize: 14,
    fontWeight: '900',
  },
});
